import { FormArray, FormBuilder, FormControl } from '@angular/forms';
import { Component, Input, OnDestroy, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import {
  ControlContainer,
  NgForm,
  Validators,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
  FormGroup,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DateAdapter } from '@angular/material/core';
import { CookieService as NgxCookieService } from 'ngx-shared-services';
import { combineLatest, distinctUntilChanged, map, Observable, startWith, Subscription, takeUntil } from 'rxjs';
import { Phone, User, UserDefaults, UserStatus, UserType } from '../shared/model/user.model';
import { Application } from '../shared/model/application.model';
import { Locale, LocaleBaseConfig } from '../shared/model/locale.model';
import { Status } from '../shared/model/status.model';
import { Timezone } from '../shared/model/timezone.model';
import { SpaceRole, UserRole } from '../shared/model/business-role.model';
import { Measurement } from '../shared/model/measurement.model';
import { ActiveLocationRolesComponent } from './active-location-roles/active-location-roles.component';
import { UserService } from '../shared/services/user.service';
import { LocalesService } from '../shared/services/locales.service';
import { UserStatusService } from '../shared/services/user-status.service';
import { TimezonesService } from '../shared/services/timezones.service';
import { BusinessRolesService } from '../shared/services/business-roles.service';
import { DeeplinksService } from '../shared/services/deeplinks.service';
import { MeasurementService } from '../shared/services/measurement.service';
import { ApplicationsService } from '../shared/services/applications.service';
import { CountriesService } from '../shared/services/countries.service';
import { UsertypeService } from '../shared/services/usertype.service';
import { BrandsService } from '../shared/services/brands.service';
import { Brand, SYSTEM_USER_BLACKLIST } from '../shared/model/brand.model';
import { ContactSubscriptionService } from '../shared/services/contact-subscriptions.service';
import { UserFormRolesService } from '../shared/services/user-form-roles.service';
import { TranslateService } from '@ngx-translate/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { LocationRolesComponent } from './location-roles/location-roles.component';
import { SubscriptionTableComponent } from '../shared/components/subscription-table/subscription-table.component';
import { UserSubmitService } from '../shared/services/user-submit.service';
import { MatStepper } from '@angular/material/stepper';
import { ExitCreateDialogComponent, ExitCreateDialogData } from '../dialogs/exit-create/exit-create-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { SubscriptionTableService } from '../shared/services/subscription-table.service';
import { UserModeService } from '../shared/services/user-mode.service';
import { UserDetail } from '../shared/model/user-detail.model';
import { UserFormService } from '../shared/services/user-form.service';
import { UserFormValidationsHelper } from '../shared/helpers/user-form-validations.helper';
import { ContactService } from '../shared/services/contact.service';
import { subscriptionChanges } from '../shared/helpers/subscription-table.helper';
import { MixPanelService } from '../shared/services/mixpanel.service';
import { ApiKeysService } from '../shared/services/api-keys.service';
import { GlobalAlertService } from '../shared/services/global-alert.service';
import { ApiKey } from '../shared/model/api-key.model';
@Component({
  selector: 'app-user-form',
  templateUrl: './user-form.component.html',
  styleUrls: ['./user-form.component.scss', '../shared/shared.styles.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
})
export class UserFormComponent implements OnInit, OnDestroy {
  readonly EDIT = 'edit';
  readonly CREATE = 'create';

  org = '';
  userId: string;
  brands: Brand[];
  subscriptions: Subscription[] = [];
  locales: Locale[];
  statuses: Status[];
  timezones: Timezone[];
  measurements: Measurement[];
  applications: Application[];
  userRoles: UserRole[];
  spacesWithRoles: SpaceRole[];
  spacesAndRoles: SpaceRole[];
  loggedInUserBrand = '';
  loggedInUserId = '';
  ecrmPattern = /^[A-Za-z0-9]{18}$/;
  syncTooltip = 'user.sync_to_ecrm_tooltip';
  unsafeSubscriptions = true;
  userLocale = 'en_US';
  deeplinkerData: any;
  savedUser: User;
  countries: any[];
  userTypes: any[];
  provinces: any[];
  // stateDisable = true;
  loadingLocationsAndRoles = false;
  loadingUserTypes = true;
  loadingBrands = true;
  showingUserPreferences = true;
  hasAnyAddressField = false;
  hasAnyPhoneField = false;
  hasAnyMobilePhoneField = false;
  hasDrUser = false;
  addressFieldValidation: boolean;
  phoneFieldValidation: boolean;
  mobilePhoneFieldValidation: boolean;
  hasEcrm: boolean;
  hasAnyVoiceNotificationRole: boolean;
  hasAnySMSNotificationRole: boolean;
  hasAnyEmailNotificationRole: boolean;
  hasAnyOfferNotificationRole: boolean;
  hasAnyPerformanceNotificationRole: boolean;
  isRetired = false;
  filteredBrands: Brand[] = [];
  filteredUserTypes: any[] = [];
  consumerAdmin = false;
  loggedInUserDetails: UserDetail;
  identificationLSV: string;
  contactInformationLSV: string;
  syncInformationLSV: string;
  userSettingsLSV: string;

  phone: Phone;
  userStatus: UserStatus;
  removedRoles = 0;
  multiLocaleConfig: LocaleBaseConfig = {
    supportedLocales: [new Locale()],
    locales: [new Locale()],
    defaultLocale: new Locale(),
    id: '',
    displayLabelKey: 'displayLabel',
    localeKey: 'localeName',
  };
  addedRoles = 0;
  firstnameLabel: '';
  firstnamePlaceholder: '';
  lastnameLabel: '';
  lastnamePlaceholder: '';
  fullnameLabel: '';
  fullnamePlaceholder: '';
  isUserActivated = false;
  shouldDefaultApp = true;

  expandableSectionsState: boolean[] = new Array(5).fill(true);
  apiKeyToken?: string;
  readonly apiKeyIdPattern = /^[A-Za-z0-9\-_]+$/;
  apiKeysGroup: FormGroup;
  showApiKeyGenerateForm = false;
  erpIconTooltip: MatIconRegistry;

  @Input() userForm: FormGroup;
  @Input() mode: string;
  @Input() change: any;

  private isValidating = false;
  isInternal: boolean;

  get syncToEcrmControl() {
    return this.userForm.get('sync_to_ecrm');
  }

  get isSaving() {
    return this.userSubmitService.submittingUser$.value;
  }

  get isEditMode() {
    return this.mode === this.EDIT;
  }

  get isCreateMode() {
    return this.mode === this.CREATE;
  }

  get emailsFormArray(): FormArray {
    return this.userForm.get('emails') as FormArray;
  }

  get phonesFormArray(): FormArray {
    return this.userForm.get('phones') as FormArray;
  }

  get addressesFormArray(): FormArray {
    return this.userForm.get('addresses') as FormArray;
  }

  get ecrmId() {
    return this.userForm.get('ecrm_id').value;
  }

  get userCountryCode(): string {
    return this.userForm.get('phones').value?.[0]?.country_code || '';
  }

  set userCountryCode(value: string) {
    if (!this.userForm.get('phones').value) {
      this.userForm
        .get('phones')
        .setValue([{ primary: false, phone_type: '', phone_number: '', extension: '', country_code: '' }]);
    }
    this.userForm.get('phones').value[0].country_code = value;
  }

  get email(): string {
    return this.userForm.get('email').value;
  }

  get userType(): string {
    return this.userForm.get('userType').value;
  }

  @ViewChild('stepper') stepper!: MatStepper;

  @ViewChild(ActiveLocationRolesComponent)
  set ActiveLocationRoles(component: ActiveLocationRolesComponent) {
    this._activeLocationRoles = component;
    if (component) {
      this.userFormRolesService.setActiveLocationRoles(component);
    }
  }
  get ActiveLocationRoles() {
    return this._activeLocationRoles;
  }
  private _activeLocationRoles: ActiveLocationRolesComponent;

  @ViewChild(LocationRolesComponent)
  set ConfigureLocationRoles(component: LocationRolesComponent) {
    this._configureLocationRoles = component;
    if (component) {
      this.userFormRolesService.setConfigureLocationRoles(component);
    }
  }
  get ConfigureLocationRoles() {
    return this._configureLocationRoles;
  }
  private _configureLocationRoles: LocationRolesComponent;

  @ViewChild(SubscriptionTableComponent)
  set SubscriptionTable(component: SubscriptionTableComponent) {
    this._subscriptionTable = component;
    if (component) {
      this.userFormRolesService.setSubscriptionTable(component);
    }
  }
  get SubscriptionTable() {
    return this._subscriptionTable;
  }

  get isOnboardingResource(): boolean {
    return this.loggedInUserDetails ? this.userModeService.isOnboarding(this.loggedInUserDetails) : false;
  }

  get isExternalLoggedInUser(): boolean {
    return this.loggedInUserDetails?.user_type === UserType.EXTERNAL;
  }

  get isExternalOwnProfile(): boolean {
    const isOwnProfile = !!(this.userId && this.loggedInUserId && this.userId === this.loggedInUserId);
    const isExternalUser = this.userType === UserType.EXTERNAL;

    return isExternalUser && isOwnProfile;
  }

  get showSyncSection(): boolean {
    return !this.isOnboardingResource && !this.isExternalLoggedInUser;
  }
  get userRolesHasChanged$() {
    return this.businessRolesService.hasChanged$;
  }

  get hasContactMethodPush() {
    return (
      this.contactService.contact$.value.contacts?.contactMethods?.some(
        (method) => method.contactMethodName === 'push',
      ) ?? false
    );
  }

  changesSubscription: Subscription;

  identificationHasChanged = false;
  contactInformationHasChanged = false;
  syncInformationHasChanged = false;
  userSettingsHasChanged = false;

  currentIndex = 0;
  isOnboarding = false;
  private _subscriptionTable: SubscriptionTableComponent;
  identificationGroup: FormGroup;
  contactInformationGroup: FormGroup;
  userSettingsGroup: FormGroup;
  syncInformationGroup: FormGroup;
  userRolesGroup: FormGroup;
  multilocaleGroup: FormGroup;

  hasCommunityRole = false;
  hasOnboardingRole = false;
  hasDRFinancialRole = false;
  // hasContactMethodPush$: Observable<boolean>;
  constructor(
    private ngxCookieService: NgxCookieService,
    private route: ActivatedRoute,
    private router: Router,
    private usersService: UserService,
    private localesService: LocalesService,
    private userStatusService: UserStatusService,
    private measurementService: MeasurementService,
    private applicationsService: ApplicationsService,
    private timezonesService: TimezonesService,
    private brandsService: BrandsService,
    private deeplinksInternalService: DeeplinksService,
    private usertypeService: UsertypeService,
    private countriesService: CountriesService,
    private businessRolesService: BusinessRolesService,
    private _adapter: DateAdapter<any>,
    private contactSubscriptionService: ContactSubscriptionService,
    private translateService: TranslateService,
    private form: NgForm,
    private iconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer,
    private userFormRolesService: UserFormRolesService,
    private changedr: ChangeDetectorRef,
    private userSubmitService: UserSubmitService,
    private dialog: MatDialog,
    private fb: FormBuilder,
    private subscriptionTableService: SubscriptionTableService,
    private userModeService: UserModeService,
    private userFormService: UserFormService,
    private contactService: ContactService,
    private mixpanelService: MixPanelService,
    private apiKeysService: ApiKeysService,
    private messageService: GlobalAlertService,
  ) {
    this.org = this.ngxCookieService.getCookie('preferred-org-id') || '';
    this.erpIconTooltip = this.iconRegistry.addSvgIcon(
      'general-info',
      this.sanitizer.bypassSecurityTrustResourceUrl('assets/svg/general-info.svg'),
    );
    const userSub = this.usersService.user$.subscribe((user) => {
      if (user && user.userId && user.userId !== '') {
        this.userId = user.userId;
        if (user.organization_id) {
          this.org = user.organization_id;
        }
      }
    });

    this.subscriptions.push(userSub);
  }

  ngOnInit() {
    this.initializeBasicSettings();
    this.initializeTranslations();
    this.setupSubscriptions();
    this.initializeServices();
    this.configureUserDefaults();
    if (this.isCreateMode) {
      this.createSubGroup();
      this.resetFormNames();
      this.monitorStepChanges(0);
    } else {
      this.validateCountry();
    }
  }

  validateCountry() {
    this.addressesFormArray.controls[0].get('country').markAsTouched();
    this.addressesFormArray.controls[0].get('country').updateValueAndValidity();
  }

  resetFormNames() {
    if (this.userForm) {
      if (this.userForm.controls['first_names']) {
        this.userForm.get('first_names').setValue({ en_US: '' });
      }
      if (this.userForm.controls['last_names']) {
        this.userForm.get('last_names').setValue({ en_US: '' });
      }
    }
    this.hasCommunityRole = false;
  }

  refreshValueAndValidity() {
    this.userForm.setErrors({ invalid: true });
    this.userForm.updateValueAndValidity({ onlySelf: false, emitEvent: true });
  }

  createSubGroup() {
    const countryControl = this.addressesFormArray.controls[0].get('country');
    const localeControl = this.userForm.get('locale');

    this.identificationGroup = this.fb.group({
      hasSaved: [false, Validators.requiredTrue],
      enel_employee_id: this.userForm.get('enel_employee_id') as FormControl,
      first_names: this.userForm.get('first_names') as FormControl,
      last_names: this.userForm.get('last_names') as FormControl,
      email: this.userForm.get('email') as FormControl,
      locale: new FormControl(localeControl.value),
      title: this.userForm.get('title') as FormControl,
      country: new FormControl(countryControl.value),
    });

    if (this.userForm.get('enel_employee_id')) {
      this.userForm.get('enel_employee_id').valueChanges.subscribe((value) => {
        this.refreshValueAndValidity();
      });
    }

    this.userForm.get('first_names').valueChanges.subscribe((value) => {
      this.refreshValueAndValidity();
    });

    this.userForm.get('last_names').valueChanges.subscribe((value) => {
      this.refreshValueAndValidity();
    });

    this.userForm.get('email').valueChanges.subscribe((value) => {
      this.refreshValueAndValidity();
    });

    this.subscriptions.push(
      countryControl.valueChanges.subscribe((value) => {
        this.identificationGroup.get('country').setValue(value, { emitEvent: true });
      }),
      localeControl.valueChanges.subscribe((value) => {
        this.identificationGroup.get('locale').setValue(value, { emitEvent: true });
        this.identificationGroup.get('locale').markAsTouched();
        this.userSettingsGroup.get('locale').setValue(value, { emitEvent: true });
        this.userSettingsGroup.get('locale').markAsTouched();
      }),
    );

    this.contactInformationGroup = this.fb.group({
      phones: this.userForm.get('phones') as FormArray,
      primary_contact: this.userForm.get('primary_contact') as FormControl,
      twentyfour_seven_contact: this.userForm.get('twentyfour_seven_contact') as FormControl,
      hasSaved: [false],
    });
    this.userSettingsGroup = this.fb.group({
      home_application: this.userForm.get('home_application') as FormControl,
      locale: new FormControl(localeControl.value),
      timezone: this.userForm.get('timezone') as FormControl,
      unit_system: this.userForm.get('unit_system') as FormControl,
      hasSaved: [false],
    });
    this.syncInformationGroup = this.fb.group({
      sync_to_ecrm: this.userForm.get('sync_to_ecrm') as FormControl,
      ecrm_contact_id: this.userForm.get('ecrm_contact_id') as FormControl,
      ecrm_user_id: this.userForm.get('ecrm_user_id') as FormControl,
      country: this.addressesFormArray.controls[0].get('country') as FormControl,
      hasSaved: [false],
    });
    this.userRolesGroup = this.fb.group({
      hasSaved: [false],
    });
    this.apiKeysGroup = this.fb.group(
      {
        api_key_id: this.userForm.get('api_key_id') as FormControl,
        key_display_label: this.userForm.get('key_display_label') as FormControl,
        hasSaved: [false],
      },
      { validators: this.userFormService.apiKeyFieldsValidator() },
    );
  }

  private initializeBasicSettings() {
    const userTypeSub = this.userForm.get('userType').valueChanges.subscribe((value) => {
      this.onUserTypeChange(value);
    });
    this.onUserTypeChange(this.userType, this.isEditMode);

    this.mobilePhoneFieldValidation = false;
    this.phoneFieldValidation = false;
    this.userFormRolesService.resetInitialState();
    this.measurements = this.measurementService.measurements;
    this.hasEcrm = this.userForm.get('sync_to_ecrm').value;
    this._adapter.setLocale(this.ngxCookieService.getCookie('locale') || 'en_US');
    this.userForm.get('sync_to_ecrm').valueChanges.subscribe((value) => {
      if (value) {
        this.userForm.get('contact_id').enable();
      } else {
        this.userForm.get('contact_id').disable();
      }
    });

    this.subscriptions.push(userTypeSub);
  }

  private initializeTranslations() {
    this.firstnameLabel = this.translateService.instant('user.first_name');
    this.firstnamePlaceholder = this.translateService.instant('user.create.placeholder.first_name');
    this.lastnameLabel = this.translateService.instant('user.last_name');
    this.lastnamePlaceholder = this.translateService.instant('user.create.placeholder.last_name');
    this.fullnameLabel = this.translateService.instant('user.full_name');
    this.fullnamePlaceholder = this.translateService.instant('user.create.placeholder.full_name');

    // stream() emits when translations first load and on every language change
    const measurementSub = this.translateService
      .stream('user.measurement')
      .subscribe(() => this.measurementService.refreshLabels());
    this.subscriptions.push(measurementSub);
  }

  private setupSubscriptions() {
    this.subscriptions.push(
      ...this.setupUserSubscriptions(),
      ...this.setupRoleSubscriptions(),
      ...this.setupPhonesValidationSubscriptions(),
      ...this.setupServiceSubscriptions(),
    );
  }

  private setupUserSubscriptions(): Subscription[] {
    const routeSub = this.route.params.subscribe(async (params) => {
      if (params.id) {
        this.userId = params.id;
        this.subscriptionTableService.enableSubscriptionData();
        this.contactSubscriptionService.getContactSubscriptions(this.userId);
      } else {
        this.subscriptionTableService.resetAndDisableSubscriptionData();
      }
    });

    const loggedInUserSub = this.usersService.loggedInUser$.subscribe(async (loggedInUser: User) => {
      if (loggedInUser) {
        this.loggedInUserBrand = loggedInUser.brand;
        this.loggedInUserId = loggedInUser.userId;
        this.userLocale = loggedInUser.locale;
        await this.localesService.getLocales();
        this.filteredBrands = this.brandsService.getFilteredBrands(this.loggedInUserBrand, this.brands);
        if (this.filteredBrands?.length <= 1) {
          this.userForm.get('brand').disable();
        } else if (this.isCreateMode) {
          this.userForm.get('brand').enable();
        }
        if (this.isCreateMode) this.setUserBrand(this.filteredBrands);
      }
    });
    const loggedUserDetails = this.usersService.loggedUserDetails$.subscribe(async (loggedUserDetails: UserDetail) => {
      if (loggedUserDetails) {
        this.loggedInUserDetails = loggedUserDetails;
      }
    });
    return [routeSub, loggedInUserSub, loggedUserDetails];
  }

  private setupRoleSubscriptions(): Subscription[] {
    const userRolesSub = this.businessRolesService.userRoles$.subscribe(({ roles }) => {
      this.userRoles = roles;
      if (roles && roles.length <= 0) {
        this.userForm.get('home_application').disable();
      } else {
        this.userForm.get('home_application').enable();
      }
      this.checkFilterRoles();
    });

    const spacesAndRolesSub = this.businessRolesService.spacesWithRoles$.subscribe((spacesWithRoles) => {
      this.spacesWithRoles = spacesWithRoles;
    });

    const hasRoleSubs = combineLatest([
      this.businessRolesService.newSpacesWithRoles$,
      this.businessRolesService.spacesWithRoles$,
    ]).subscribe(([newSpaces, spaces]) => {
      this.hasCommunityRole =
        this.userFormRolesService.hasUserRole(this.userRoles, 'COMMUNITY_USER') ||
        this.hasBeenAddedRole(newSpaces, spaces, 'COMMUNITY_USER');
      this.updateEcrmIdValidation();

      this.hasOnboardingRole =
        this.userFormRolesService.hasUserRole(this.userRoles, 'ONBOARDING') ||
        this.hasBeenAddedRole(newSpaces, spaces, 'ONBOARDING');

      const hasNewOnboardingRole = this.hasBeenAddedNewRole(newSpaces, spaces, 'ONBOARDING');

      if (hasNewOnboardingRole) {
        this.onOnboardingRoleAdd();
      }

      this.hasDRFinancialRole =
        this.userFormRolesService.hasUserRole(this.userRoles, 'DR_FINANCIAL_USER') ||
        this.hasBeenAddedRole(newSpaces, spaces, 'DR_FINANCIAL_USER');

      const hasNewDRFinancialRole = this.hasBeenAddedNewRole(newSpaces, spaces, 'DR_FINANCIAL_USER');

      if (hasNewDRFinancialRole) {
        this.onDRFinancialRoleAdd();
      }
    });

    return [userRolesSub, spacesAndRolesSub, hasRoleSubs];
  }

  hasBeenAddedRole(newSpaces: SpaceRole[], spaces: SpaceRole[], addedRole: string): boolean {
    return (
      newSpaces.some((space) => space.newRoles.some((role: any) => role.name === addedRole)) ||
      spaces.some(
        (space) =>
          space.activeRoles.some((role) => role.businessRole === addedRole) ||
          spaces.some((space) => space.newRoles.some((role) => role.businessRole === addedRole)),
      )
    );
  }

  hasBeenAddedNewRole(newSpaces: SpaceRole[], spaces: SpaceRole[], addedRole: string): boolean {
    return (
      newSpaces.some((space) => space.newRoles.some((role: any) => role.name === addedRole)) ||
      spaces.some((space) => space.newRoles.some((role) => role.businessRole === addedRole))
    );
  }

  private setupPhonesValidationSubscriptions(): Subscription[] {
    //Trigger validation on load
    this.phoneFieldValidationCheck();
    const phoneFieldSub = this.phonesFormArray.valueChanges.subscribe(() => {
      if (!this.isValidating) {
        this.isValidating = true;
        this.phoneFieldValidationCheck();
        this.isValidating = false;
      }
    });
    return [phoneFieldSub];
  }

  private setupServiceSubscriptions(): Subscription[] {
    const statusesSub = this.userStatusService.statuses$.subscribe((statuses) => (this.statuses = statuses));
    const localesSub = this.setupLocalesSubscription();
    const timezonesSub = this.timezonesService.timezones$.subscribe((timezones) => (this.timezones = timezones));
    const brandsSub = this.brandsService.brands$.subscribe((brands) => {
      this.loadingBrands = false;
      this.brands = brands;
    });
    const countriesSub = this.setupCountriesSubscription();
    const userTypesSub = this.setupUserTypesSubscription();
    const applicationsSub = this.setupApplicationsSubscription();

    const subs = [statusesSub, localesSub, timezonesSub, countriesSub, brandsSub, userTypesSub, applicationsSub];

    if (!this.isCreateMode) {
      const apiKeysSub = this.apiKeysService.apiKeys$.subscribe((apiKey: ApiKey) => {
        if (apiKey && apiKey.keyId && apiKey.displayLabel) {
          this.apiKeyToken = apiKey.token;
          this.userForm.patchValue({
            api_key_id: apiKey.keyId,
            key_display_label: apiKey.displayLabel,
          });
          this.userForm.get('api_key_id').disable();
          this.userForm.get('key_display_label').disable();
        } else {
          this.userForm.get('api_key_id').enable();
          this.userForm.get('key_display_label').enable();
        }
      });
      subs.push(apiKeysSub);
    }

    return subs;
  }

  private setupLocalesSubscription(): Subscription {
    return this.localesService.locales$.subscribe((locales) => {
      if (locales?.length) {
        this.locales = locales;
        const locale = this.localesService.checkForLocale(this.userLocale, locales) || locales[0];
        this.multiLocaleConfig.defaultLocale = locale;

        if (this.isCreateMode) {
          const supportedLocales = new Set([locales[0], locale]);
          this.multiLocaleConfig.supportedLocales = Array.from(supportedLocales);
        } else if (this.isEditMode) {
          this.buildSupportedLocales();
        }
      }
    });
  }

  private setupCountriesSubscription(): Subscription {
    return this.countriesService.countries$.subscribe((countries) => {
      this.countries = countries;
    });
  }

  private setupUserTypesSubscription(): Subscription {
    return this.usertypeService.userTypes$.subscribe(async (userTypes) => {
      await this.usersService.getLoggedInUser();
      this.userTypes = userTypes;
      this.filteredUserTypes = this.usertypeService.filterTypesByBrand(userTypes);
      if (this.isSystemUser()) {
        this.filteredUserTypes = this.filteredUserTypes.filter((type) => !SYSTEM_USER_BLACKLIST.includes(type.name));
      }
      this.loadingUserTypes = false;
    });
  }

  private setupApplicationsSubscription(): Subscription {
    return this.applicationsService.applications$.subscribe((applications) => {
      this.applications = applications;
      this.handleApplicationsUpdate(applications);
    });
  }

  private handleApplicationsUpdate(applications: Application[]) {
    if (applications.length <= 1) {
      this.userForm.get('home_application').disable();
    } else {
      this.userForm.get('home_application').enable();
    }
    if (this.isCreateMode && applications.length > 0 && !this.userForm.get('home_application').value) {
      this.setDefaultHomeApplication(applications);
    }
    if (this.userId && this.shouldResetHomeApplication()) {
      this.userForm.get('home_application').setValue('PROFILE');
    }
    this.checkFilterRoles();
  }

  private setDefaultHomeApplication(applications: Application[]) {
    const profileApp = applications.find((app) => app.name === 'PROFILE');
    if (profileApp) {
      this.userForm.get('home_application').setValue(profileApp.name);
    } else {
      this.userForm.get('home_application').setValue(applications[0].name);
    }
  }

  private shouldResetHomeApplication(): boolean {
    const currentApp = this.userForm.get('home_application').value;
    return !currentApp || !this.applications.find((app) => app.name === currentApp);
  }

  private initializeServices() {
    const flexContactId = this.userForm.get('flex_contact_id').value;
    this.contactService.getContact(flexContactId);
    // this.hasContactMethodPush$ = this.contactService.contact$.pipe(
    //   map((contact) => {
    //     console.log(contact);
    //     return contact.contacts?.contactMethods?.includes('push') ?? false;
    //   }),
    // );
    this.measurementService.setMeasurements();
    this.deeplinksInternalService.setMarketAdminUrl();

    if (!this.isCreateMode && this.userId) {
      this.applicationsService.getApplicationsByUser(this.userId);
      if (this.isSystemUser()) {
        this.apiKeysService.getKeys(this.org);
      }
    } else {
      this.applicationsService.getApplications();
    }
    this.handleEcrmChange();
  }

  checkFilterRoles() {
    if (this.userRoles && this.applications && this.consumerAdmin) {
      const userIsConsumerAdmin =
        this.userRoles.find((role) => role.businessRole === 'CONSUMERS_USER_ADMIN') !== undefined;
      if (this.consumerAdmin && !userIsConsumerAdmin) {
        this.applications = this.applications.filter((application) => {
          return application.name !== 'GLOBAL_ADMIN_USER_ADMIN';
        });
        if (this.isCreateMode && this.applications.length > 0) {
          this.userForm.get('home_application').setValue(this.applications[0].name);
        }
      }
    }
    if (this.isCreateMode && !this.userRoles && this.applications && this.consumerAdmin) {
      this.applications = this.applications.filter((application) => {
        return application.name !== 'GLOBAL_ADMIN_USER_ADMIN';
      });
    }
    if (this.isCreateMode && this.applications && this.shouldDefaultApp) {
      this.userForm.get('home_application').setValue('PROFILE');
      this.shouldDefaultApp = false;
    }
  }

  setUserBrand(brands: Brand[]) {
    this.userForm
      .get('brand')
      .setValue(
        brands.find((item) => item.name === this.loggedInUserDetails.brand)?.name || this.filteredBrands[0]?.name,
      );
  }

  async applyRoles(state) {
    this.loadingLocationsAndRoles = true;
    if (!state) {
      this.spacesAndRoles = await this.userFormRolesService.applyRoles(this.userId, state);
      this.loadingLocationsAndRoles = false;
    }
  }

  hasRole(roles, searchRole) {
    return this.userFormRolesService.hasUserRole(roles, searchRole);
  }

  resetDeeplinkerData() {
    this.deeplinkerData = {};
  }

  updateEcrmIdValidation() {
    const ecrmIdControl = this.userForm.get('ecrm_id');

    if (this.hasCommunityRole) {
      ecrmIdControl.enable();
      ecrmIdControl.setValidators([
        Validators.pattern(this.ecrmPattern),
        this.startsWithValidator('005'),
        Validators.required,
      ]);
    } else {
      ecrmIdControl.disable();
      ecrmIdControl.setValidators(null);
    }
    ecrmIdControl.markAsTouched();
    ecrmIdControl.markAsDirty();
    ecrmIdControl.updateValueAndValidity();
  }

  getUserTypeForUser() {
    if (this.userTypes && this.userTypes.length > 0 && this.userForm.get('userType').value) {
      return (
        this.userTypes.find((userType) => userType.name === this.userForm.get('userType').value).displayLabel || ''
      );
    }
  }

  handleEcrmChange() {
    this.hasEcrm = this.userForm.get('sync_to_ecrm').value;
  }

  phoneFieldValidationCheck() {
    const result = this.userFormService.phoneFieldValidationCheck(
      this.userForm,
      this.hasAnyVoiceNotificationRole,
      this.hasAnySMSNotificationRole,
    );

    this.hasAnyPhoneField = result.hasAnyPhoneField;
    this.phoneFieldValidation = result.phoneFieldValidation;
    this.hasAnyMobilePhoneField = result.hasAnyMobilePhoneField;
    this.mobilePhoneFieldValidation = result.mobilePhoneFieldValidation;
  }

  onCountryChange(event: any) {
    this.addressesFormArray.controls[0].patchValue({ country: event?.value, country_name: event?.value });
    this.userFormService.addressFieldValidationCheck(this.addressesFormArray, event?.value || '');
  }

  isNotOwnProfile() {
    if (this.userId && this.loggedInUserId) {
      return this.userId !== this.loggedInUserId;
    }
    return false;
  }

  toggleUserPreferences() {
    this.showingUserPreferences = !this.showingUserPreferences;
  }

  async newSubmitRoles() {
    if (!this.isNotOwnProfile()) {
      return;
    }

    try {
      await this.userFormRolesService.newSubmitRoles(
        this.userId,
        this.ActiveLocationRoles,
        this.ConfigureLocationRoles,
        this.userForm.get('userType').value,
      );
      this.userRolesGroup?.get('hasSaved').setValue(true);
      if (this.ActiveLocationRoles.errorRoleArray.length > 0) {
        this.addedRoles = this.ActiveLocationRoles.errorRoleArray.length;
      }
    } catch (error) {
      console.log(error);
    }
  }

  buildSupportedLocales() {
    if (this.multiLocaleConfig.supportedLocales && this.userForm.get('userType').value && this.locales) {
      this.multiLocaleConfig.supportedLocales = [];
      const keys = Object.keys(this.userForm.get('first_names').value);
      for (const key of keys) {
        const localeFound = this.locales.find((locale) => locale.localeName === key);
        if (localeFound) {
          this.multiLocaleConfig.supportedLocales.push(localeFound);
        }
      }
    }
  }

  onSupportedLocaleChange() {
    //FORCE en_US TO BE ALWAYS BE SUPPORTED. IT IS REQUIRED IN ATLAS.
    const required = this.multiLocaleConfig.defaultLocale;

    const exists = this.multiLocaleConfig.supportedLocales.some((l) => l.localeId === required.id);

    if (!exists) {
      this.multiLocaleConfig.supportedLocales = [required, ...this.multiLocaleConfig.supportedLocales];
    }

    if (this.multiLocaleConfig.supportedLocales.length === 0) {
      this.multiLocaleConfig.supportedLocales = [
        ...this.multiLocaleConfig.supportedLocales,
        this.multiLocaleConfig.defaultLocale,
      ];
    } else if (this.multiLocaleConfig.supportedLocales.length === 1) {
      this.userForm.get('locale').setValue(this.multiLocaleConfig.supportedLocales[0].localeName);
    }
    this.form.form.controls['localeInputsForm_user_first_name']?.markAsDirty();
    this.form.form.controls['localeInputsForm_user_last_name']?.markAsDirty();
    setTimeout(() => {
      const firstNamesControl = this.userForm.get('first_names');
      const lastNamesControl = this.userForm.get('last_names');
      firstNamesControl.setErrors({ invalid: true });
      firstNamesControl.markAsTouched();
      firstNamesControl.markAsDirty();
      firstNamesControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
      lastNamesControl.setErrors({ invalid: true });
      lastNamesControl.markAsTouched();
      lastNamesControl.markAsDirty();
      lastNamesControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
      this.userForm.setErrors({ invalid: true });
      this.userForm.updateValueAndValidity({ onlySelf: false, emitEvent: true });
    });
  }

  limitLocaleSelectList(num: number, locale: Locale) {
    return (
      (this.multiLocaleConfig.supportedLocales.length > num - 1 &&
        !this.multiLocaleConfig.supportedLocales.includes(locale)) ||
      locale.id === '1'
    );
  }

  isEcrmContactEnabled() {
    return this.userForm.get('sync_to_ecrm').value && this.userForm.get('last_sync_date').value !== undefined;
  }

  isDeactivatedUser() {
    return this.userForm.get('status').value.toUpperCase() === 'DEACTIVATED';
  }

  expandAll() {
    this.expandableSectionsState = new Array(this.expandableSectionsState.length).fill(true);
  }

  collapseAll() {
    this.expandableSectionsState = new Array(this.expandableSectionsState.length).fill(false);
  }

  expansionOpened(i) {
    this.expandableSectionsState[i] = true;
  }

  expansionClosed(i) {
    this.expandableSectionsState[i] = false;
  }

  onRoleRemoval($event) {
    switch ($event) {
      case 'new':
        this.addedRoles -= 1;
        break;
      case 'resetNew':
        this.addedRoles = 0;
        this.removedRoles = 0;
        this.ConfigureLocationRoles.deselectAll();
        break;
      default:
        this.removedRoles += 1;
    }
  }

  onSiteRemoval($event) {
    this.addedRoles -= $event[0];
    this.removedRoles = 0;
    let delroles = 0;
    setTimeout(() => {
      this.ActiveLocationRoles.activeLocations.forEach((space) => {
        delroles += space.roleChips.filter((chip) => chip.toDelete).length;
      });
      this.removedRoles = delroles;
    }, 10);
  }

  onRoleAdd() {
    let newRoles = 0;

    this.ActiveLocationRoles.activeLocations.forEach((space) => {
      newRoles += space.spaceWithRoles.newRoles.length;
    });
    this.addedRoles = newRoles;
  }

  previusSendActivationValue = false;
  isSendActivationStatusEnabled = false;

  onSystemLevelChange($event) {
    if ($event.checked) {
      this.previusSendActivationValue = this.userForm.get('send_activation').value;
      this.isSendActivationStatusEnabled = this.userForm.get('send_activation').disabled;
      this.userForm.get('send_activation').disable();
      this.userForm.get('send_activation').setValue(false);
      this.userForm.get('userClass').setValue('SYSTEM');
      this.filteredUserTypes = this.filteredUserTypes.filter((type) => !SYSTEM_USER_BLACKLIST.includes(type.name));
      if (this.userForm.get('userType').value && SYSTEM_USER_BLACKLIST.includes(this.userForm.get('userType').value)) {
        this.userForm.get('userType').setValue(null);
      }
      this.userForm.get('api_key_id').setValidators([Validators.pattern(this.apiKeyIdPattern)]);
      this.userForm.get('api_key_id').updateValueAndValidity();
      this.userForm.get('key_display_label').clearValidators();
      this.userForm.get('key_display_label').updateValueAndValidity();
    } else {
      this.userForm.get('send_activation').setValue(this.previusSendActivationValue);
      if (this.isSendActivationStatusEnabled) {
        this.userForm.get('send_activation').enable();
      }
      this.userForm.get('userClass').setValue('PERSON');
      this.userForm.get('asset_manager').setValue(false);
      this.userForm.get('asset_manager').enable();
      this.usertypeService.getUserTypes();
      this.userForm.get('api_key_id').clearValidators();
      this.userForm.get('api_key_id').updateValueAndValidity();
      this.userForm.get('key_display_label').clearValidators();
      this.userForm.get('key_display_label').updateValueAndValidity();
    }
  }

  isSystemUser() {
    return this.userForm.get('userClass').value === 'SYSTEM';
  }

  unRemoveRole($event) {
    this.removedRoles -= $event;
  }

  addSubscription(sub) {
    this.userFormRolesService.addSubscription(sub);
    this.hasAnySMSNotificationRole = this.userFormRolesService.hasAnySMSNotificationRole;
    this.hasAnyEmailNotificationRole = this.userFormRolesService.hasAnyEmailNotificationRole;
    this.hasAnyVoiceNotificationRole = this.userFormRolesService.hasAnyVoiceNotificationRole;
    this.hasAnyOfferNotificationRole = this.userFormRolesService.hasAnyOfferNotificationRole;
    this.hasAnyPerformanceNotificationRole = this.userFormRolesService.hasAnyPerformanceNotificationRole;
    this.phoneFieldValidationCheck();
  }

  removeSubscription(sub) {
    this.userFormRolesService.removeSubscription(sub);
    this.hasAnySMSNotificationRole = this.userFormRolesService.hasAnySMSNotificationRole;
    this.hasAnyEmailNotificationRole = this.userFormRolesService.hasAnyEmailNotificationRole;
    this.hasAnyVoiceNotificationRole = this.userFormRolesService.hasAnyVoiceNotificationRole;
    this.hasAnyOfferNotificationRole = this.userFormRolesService.hasAnyOfferNotificationRole;
    this.hasAnyPerformanceNotificationRole = this.userFormRolesService.hasAnyPerformanceNotificationRole;
    this.phoneFieldValidationCheck();
  }

  onECRMContactIDChange() {
    const contactIdControl = this.userForm.get('contact_id');

    contactIdControl.setValidators([
      Validators.pattern(this.ecrmPattern),
      this.startsWithValidator('003'),
      Validators.required,
    ]);
    contactIdControl.updateValueAndValidity();
  }

  onECRMUserIDChange() {
    const ecrmIdControl = this.userForm.get('ecrm_id');

    ecrmIdControl.setValidators([
      Validators.pattern(this.ecrmPattern),
      this.startsWithValidator('005'),
      Validators.required,
    ]);
    ecrmIdControl.updateValueAndValidity();
  }

  startsWithValidator(prefix: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value || control.value.startsWith(prefix)) {
        return null;
      }
      return { start: true };
    };
  }

  saveRoles() {
    this.ActiveLocationRoles.openSubmitRolesDialog();
  }

  resetRoles() {
    this.ActiveLocationRoles.resetRoles();
  }

  disableUserClass() {
    this.userForm.get('userClass').disable();
  }

  async saveIdentification() {
    let result;
    if (!this.identificationGroup.get('hasSaved').value) {
      this.mixpanelService.userCreateStep(1);
      result = await this.userSubmitService.handleCreateSubmit(this.userForm);
    } else if (this.identificationHasChanged) {
      result = await this.userSubmitService.handleEditSubmit(this.userForm, this.userId, false, false);
    }
    if (result?.created) {
      this.mixpanelService.userCreateStepSuccess();
      this.identificationGroup.get('hasSaved').setValue(true);
      this.subscriptionTableService.enableSubscriptionData();
      this.applicationsService.getApplicationsByUser(result.userId);
      this.setFormAsEdit();
      this.disableUserClass();

      if (this.isSystemUser()) {
        const keyId = this.userForm.get('api_key_id')?.value?.trim();
        const displayLabel = this.userForm.get('key_display_label')?.value?.trim();

        if (keyId && displayLabel) {
          const apiKeyCreated = await this.saveApiKeys();
          if (apiKeyCreated) {
            this.stepper.next();
          }
        } else {
          this.stepper.next();
        }
      } else this.stepper.next();
    }
    this.resetSavedValues();
  }

  contactInformationStepHasChanged(): boolean {
    return !(
      this.contactInformationHasChanged ||
      (this.userForm.get('send_activation').value && !this.isUserActivated) ||
      this.subscriptionTableService.hasChanges$.value
    );
  }

  async saveContactInformation() {
    if (!this.isUserActivated && this.userForm.get('send_activation').value) {
      try {
        await this.userSubmitService.handleSendActivationEmail(this.userId);
        this.isUserActivated = true;
        this.userForm.get('send_activation').disable();
      } catch (err) {
        return;
      }
    }
    let updated = false;
    const contactInformationHasChangedAndTouched =
      this.contactInformationHasChanged && this.contactInformationGroup.touched;
    const subscriptionTableHasChanges = this.subscriptionTableService.hasChanges$.value;
    if (contactInformationHasChangedAndTouched) {
      const result = await this.userSubmitService.handleEditSubmit(this.userForm, this.userId, false, false);
      updated = result.updated;
    }
    if (subscriptionTableHasChanges) {
      await this.userSubmitService.handleSubscriptionsSubmit(this.userForm, this.userId);
      this.contactSubscriptionService.getContactSubscriptions(this.userId);
    }

    if (contactInformationHasChangedAndTouched || subscriptionTableHasChanges) {
      this.mixpanelService.userCreateStep(3);
    }

    if (updated) {
      this.contactInformationHasChanged = false;
      this.contactInformationGroup.markAsUntouched();
      this.resetSavedValues();
    }
  }

  async saveSyncInformation() {
    if (this.syncInformationHasChanged && this.syncInformationGroup.touched) {
      await this.userSubmitService.handleEditSubmit(this.userForm, this.userId, false, false);
      if (this.syncToEcrmControl.value) {
        this.syncToEcrmControl.disable();
      }
      this.syncInformationGroup.markAsUntouched();
      this.syncInformationHasChanged = false;
      this.resetSavedValues();
    }
  }

  async saveApiKeys(): Promise<boolean> {
    const keyId = this.userForm.get('api_key_id').value;
    const displayLabel = this.userForm.get('key_display_label').value;
    try {
      const result = await this.apiKeysService.updateKey(keyId, {
        account_id: this.org,
        is_active: true,
        sources: [],
        display_label: displayLabel,
      });
      if (result && result.token) {
        this.apiKeyToken = result.token;
        this.apiKeysService.apiKeys$.next(result);
        return true;
      }
    } catch (err) {
      this.messageService.setError(this.translateService.instant('user.api_key_error'));
    }
    return false;
  }

  async createApiKey() {
    const keyId = this.userForm.get('api_key_id').value;
    const displayLabel = this.userForm.get('key_display_label').value;
    try {
      const result = await this.apiKeysService.updateKey(keyId, {
        account_id: this.org,
        is_active: true,
        sources: [],
        display_label: displayLabel,
      });
      if (result && result.token) {
        this.apiKeyToken = result.token;
        this.apiKeysService.apiKeys$.next(result);
      }
    } catch (err) {
      this.messageService.setError(this.translateService.instant('user.api_key_error'));
    }
  }

  onGenerateApiKeyClick() {
    const apiKeyIdControl = this.userForm.get('api_key_id');
    const keyDisplayLabelControl = this.userForm.get('key_display_label');

    // Set required + pattern validators on api_key_id
    apiKeyIdControl.setValidators([Validators.required, Validators.pattern(this.apiKeyIdPattern)]);
    // Set required validator on key_display_label
    keyDisplayLabelControl.setValidators([Validators.required]);

    // Enable both fields for input
    apiKeyIdControl.enable();
    keyDisplayLabelControl.enable();

    // Update validity on both controls
    apiKeyIdControl.updateValueAndValidity();
    keyDisplayLabelControl.updateValueAndValidity();

    // Show the confirm/save button
    this.showApiKeyGenerateForm = true;
  }

  async saveUserSettings() {
    if (this.userSettingsHasChanged && this.userSettingsGroup.touched) {
      this.mixpanelService.userCreateStep(4);
      await this.userSubmitService.handleEditSubmit(this.userForm, this.userId, false, false);

      this.userSettingsGroup.markAsUntouched();
      this.userSettingsHasChanged = false;
      this.resetSavedValues();
    }
  }

  resetSavedValues() {
    this.identificationLSV = JSON.stringify(this.identificationGroup.value);
    if (this.syncInformationLSV) {
      this.syncInformationLSV = JSON.stringify(this.syncInformationGroup.value);
    }
    if (this.userSettingsLSV) {
      this.userSettingsLSV = JSON.stringify(this.userSettingsGroup.value);
    }
    if (this.contactInformationLSV) {
      this.contactInformationLSV = JSON.stringify(this.contactInformationGroup.value);
    }
    this.contactInformationGroup = this.fb.group({
      phones: this.userForm.get('phones') as FormArray,
      primary_contact: this.userForm.get('primary_contact') as FormControl,
      twentyfour_seven_contact: this.userForm.get('twentyfour_seven_contact') as FormControl,
      hasSaved: [false],
    });
    this.phoneFieldValidationCheck();
    this.subscriptions.push(...this.setupPhonesValidationSubscriptions());
    this.monitorStepChanges(this.currentIndex);
  }

  setFormAsEdit() {
    this.userForm.markAsPristine();
    this.userForm.markAsUntouched();
    this.userForm.get('username').disable();
    this.userForm.get('userType').disable();
    this.userForm.get('brand').disable();
  }

  isUserCreated() {
    return this.identificationGroup.get('hasSaved').value;
  }

  handleExit(selectedId: string) {
    const sectionSaved = this.checkCurrentSectionSaved();
    const sectionHasChanged = this.checkCurrentSectionHasChanged();
    console.log('sectionSaved', sectionSaved);
    console.log('sectionHasChanged', sectionHasChanged);
    const data: ExitCreateDialogData = {
      currentSection: this.stepper.selectedIndex,
      selectedId: selectedId,
      userId: this.userId,
      isCreated: this.isUserCreated(),
      sectionSaved: sectionSaved,
      sectionHasChanged: sectionHasChanged,
    };

    if (this.isOnboarding && this.stepper.selectedIndex === 3) {
      data.currentSection += 1;
    }

    if (!sectionHasChanged && this.isUserCreated()) {
      console.log('redirecting to view');
      this.router.navigate([`details/${this.userId}/view`], {});
    } else {
      this.dialog.open(ExitCreateDialogComponent, {
        data: data,
      });
    }
  }

  onStepChange(event: any) {
    this.currentIndex = event.selectedIndex;
    this.augmentStepperForOnboardingUser();
    if (this.isOnboarding && event.selectedIndex === 3) {
      this.monitorStepChanges(event.selectedIndex + 1);
    } else {
      this.monitorStepChanges(event.selectedIndex);
    }
  }

  monitorStepChanges(index: number) {
    this.identificationHasChanged = false;
    this.contactInformationHasChanged = false;
    this.syncInformationHasChanged = false;
    this.userSettingsHasChanged = false;
    this.changesSubscription?.unsubscribe();
    switch (index) {
      case 0:
        this.changesSubscription = this.identificationGroup.valueChanges.subscribe((value) => {
          this.identificationHasChanged = JSON.stringify(value) !== this.identificationLSV;
          console.log('identificationHasChanged', this.identificationHasChanged);
        });
        break;
      case 2:
        if (!this.contactInformationLSV) {
          this.contactInformationLSV = JSON.stringify(this.contactInformationGroup.value);
        }
        this.changesSubscription = this.contactInformationGroup.valueChanges.subscribe((value) => {
          this.contactInformationHasChanged = JSON.stringify(value) !== this.contactInformationLSV;
          console.log('contactInformationHasChanged', this.contactInformationHasChanged);
        });
        break;
      case 3:
        if (!this.syncInformationLSV) {
          this.syncInformationLSV = JSON.stringify(this.syncInformationGroup.value);
        }
        this.changesSubscription = this.syncInformationGroup.valueChanges.subscribe((value) => {
          this.syncInformationHasChanged = JSON.stringify(value) !== this.syncInformationLSV;
          console.log('syncInformationHasChanged', this.syncInformationHasChanged);
        });
        break;
      case 4:
        if (!this.userSettingsLSV) {
          this.userSettingsLSV = JSON.stringify(this.userSettingsGroup.value);
        }
        this.changesSubscription = this.userSettingsGroup.valueChanges.subscribe((value) => {
          this.userSettingsHasChanged = JSON.stringify(value) !== this.userSettingsLSV;
          console.log('userSettingsHasChanged', this.userSettingsHasChanged);
        });
        break;
    }
  }

  checkCurrentSectionSaved() {
    const step = this.augmentStepperForOnboardingUser();

    switch (step) {
      case 0:
        return this.identificationGroup.get('hasSaved').value;
      case 1:
        return this.userRolesGroup.get('hasSaved').value;
      case 2:
        return this.contactInformationGroup.get('hasSaved').value;
      case 3:
        return this.syncInformationGroup.get('hasSaved').value;
      case 4:
        return this.userSettingsGroup.get('hasSaved').value;
    }
  }

  checkCurrentSectionHasChanged() {
    const step = this.augmentStepperForOnboardingUser();
    switch (step) {
      case 0:
        return this.identificationHasChanged;
      case 1:
        return this.checkUserRolesHasChanged();
      case 2:
        return this.checkContactInformationHasChanged();
      case 3:
        return this.syncInformationHasChanged;
      case 4:
        return this.userSettingsHasChanged;
    }
  }

  augmentStepperForOnboardingUser() {
    let step = this.stepper.selectedIndex;
    if (this.isOnboarding && this.stepper.selectedIndex === 3) {
      step = this.stepper.selectedIndex + 1;
    }

    return step;
  }

  checkUserRolesHasChanged() {
    const { totalAddedRoles, totalDeletedRoles } = this.ActiveLocationRoles.calculateAddedAndDeletedRoles();
    return totalAddedRoles > 0 || totalDeletedRoles > 0;
  }

  checkContactInformationHasChanged() {
    const subscriptionsChanged =
      subscriptionChanges(this.SubscriptionTable.dataSource.data, this.SubscriptionTable.previousSubscriptions).length >
      0;
    return this.contactInformationHasChanged || subscriptionsChanged;
  }

  private configureUserDefaults(): void {
    if (this.loggedInUserDetails) {
      this.isOnboarding = this.userModeService.isOnboarding(this.loggedInUserDetails);
      if (this.isOnboarding) {
        this.userForm.get('userType').setValue(this.userModeService.getDefaultUserType(this.loggedInUserDetails));
        this.userForm.get('brand').setValue(this.userModeService.getDefaultBrand(this.loggedInUserDetails));
        this.userForm.get('sync_to_ecrm').setValue(true);
      }
    }
  }

  ngOnDestroy() {
    this.changesSubscription?.unsubscribe();
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }

  onOnboardingRoleAdd() {
    this.SubscriptionTable.addSubscriptionForRole('reminders');
  }

  onDRFinancialRoleAdd() {
    this.SubscriptionTable.addSubscriptionForRole('payment');
  }

  onUserTypeChange(value: string, isEdit: boolean = false) {
    this.isInternal = UserFormValidationsHelper.applyEnelEmployeeIdValidation(this.userForm, value, isEdit);
  }
}
